import { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Radio } from "lucide-react";
import api from "@/lib/api";

const ActivityContext = createContext({ tick: 0 });

export function ActivityProvider({ children }) {
  const [tick, setTick] = useState(0);
  const seen = useRef(new Set());
  const initialized = useRef(false);
  const audioCtx = useRef(null);

  const beep = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      audioCtx.current = audioCtx.current || new AC();
      const ctx = audioCtx.current;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      o.type = "sine";
      o.frequency.setValueAtTime(1046, ctx.currentTime);
      o.frequency.exponentialRampToValueAtTime(1568, ctx.currentTime + 0.12);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.6, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
      o.start();
      o.stop(ctx.currentTime + 0.32);
    } catch (e) {
      /* audio not available */
    }
  };

  const horn = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      audioCtx.current = audioCtx.current || new AC();
      const ctx = audioCtx.current;
      const now = ctx.currentTime;
      [[370, 0], [311, 0.16]].forEach(([f, t]) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sawtooth";
        o.frequency.value = f;
        o.connect(g);
        g.connect(ctx.destination);
        g.gain.setValueAtTime(0.0001, now + t);
        g.gain.exponentialRampToValueAtTime(0.7, now + t + 0.03);
        g.gain.setValueAtTime(0.7, now + t + 0.3);
        g.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.5);
        o.start(now + t);
        o.stop(now + t + 0.52);
      });
    } catch (e) {
      /* audio not available */
    }
  };

  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/activity");
        if (!active) return;
        const items = data.items || [];
        if (!initialized.current) {
          items.forEach((i) => seen.current.add(i.id));
          initialized.current = true;
          return;
        }
        const fresh = items.filter((i) => !seen.current.has(i.id));
        if (fresh.length) {
          const hasHorn = fresh.some((i) => i.sound === "horn");
          fresh.reverse().forEach((i) => {
            seen.current.add(i.id);
            toast(i.message, {
              description: "Aggiornamento in tempo reale",
              icon: <Radio className="h-4 w-4 text-cyan-400" />,
            });
          });
          if (hasHorn) horn(); else beep();
          setTick((t) => t + 1);
        }
      } catch (e) {
        /* ignore */
      }
    };
    poll();
    const iv = setInterval(poll, 8000);
    return () => {
      active = false;
      clearInterval(iv);
    };
  }, []);

  return <ActivityContext.Provider value={{ tick }}>{children}</ActivityContext.Provider>;
}

export const useActivity = () => useContext(ActivityContext);
